import React from 'react';
import { PiMagnifyingGlass } from 'react-icons/pi';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import {
  ComponentSize,
  ComponentVariant,
  ComponentAppearance,
} from 'src/modules/shared/types/component.type';

interface NumberRangeFilterProps {
  /** Single label describing the range (e.g. "Precio venta") */
  label: string;
  placeholderFrom?: string;
  placeholderTo?: string;
  valueFrom?: number;
  valueTo?: number;
  onChange: (next: { from?: number; to?: number }) => void;
  onApply: () => void;
  /** Semantic variant for the component */
  variant?: ComponentVariant;
  /** Visual appearance style */
  appearance?: ComponentAppearance;
  /** Component size */
  size?: ComponentSize;
  /** Custom CSS classes */
  className?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
}

/**
 * Numeric range filter with two inputs (from/to) and an explicit
 * apply button. Intended for use in filter toolbars where changes
 * should only be applied when the user confirms.
 */
export const NumberRangeFilter: React.FC<NumberRangeFilterProps> = ({
  label,
  placeholderFrom = '0',
  placeholderTo = '0',
  valueFrom,
  valueTo,
  onChange,
  onApply,
  variant = 'primary',
  appearance = 'filled',
  size = 'md',
  className = '',
  disabled = false,
}) => {
  const parseNumberInput = (raw: string): number | undefined => {
    const trimmed = raw.replace(/\s+/g, '');
    if (!trimmed) return undefined;
    const normalized = trimmed.replace(',', '.');
    const asNumber = Number(normalized);
    return Number.isFinite(asNumber) ? asNumber : undefined;
  };

  const iconSizeClasses: Record<ComponentSize, string> = {
    '2xs': 'h-3 w-3',
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <div className={`flex w-full flex-col gap-1 ${className}`}>
      <div className="flex items-end gap-1">
        <div className="flex-1">
          <LabelTextInput
            label={label}
            size={size}
            variant={variant}
            appearance={appearance}
            disabled={disabled}
            labelPosition="top"
            inputProps={{
              placeholder: placeholderFrom,
              value: typeof valueFrom === 'number' ? String(valueFrom) : '',
              onChange: (event) =>
                onChange({
                  from: parseNumberInput(event.target.value),
                  to: valueTo,
                }),
            }}
          />
        </div>
        <div className="flex-1">
          <LabelTextInput
            label=""
            size={size}
            variant={variant}
            appearance={appearance}
            disabled={disabled}
            labelPosition="top"
            inputProps={{
              placeholder: placeholderTo,
              value: typeof valueTo === 'number' ? String(valueTo) : '',
              onChange: (event) =>
                onChange({
                  from: valueFrom,
                  to: parseNumberInput(event.target.value),
                }),
            }}
          />
        </div>
        <Button
          variant={variant}
          appearance="soft"
          size={size}
          type="button"
          onClick={onApply}
          disabled={disabled}
          isIconOnly
        >
          <PiMagnifyingGlass className={iconSizeClasses[size]} />
        </Button>
      </div>
    </div>
  );
};
